import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Provider } from "@redotech/redo-order-common/provider";
import { SPECIAL_RESTOCK_ID } from "@redotech/redo-return-core-common/return-flow";
import { SPECIAL_LOCATION_ID } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { Dispatch, SetStateAction, useContext } from "react";

interface LocationSelectionState {
  locations: string[];
  restockExternalLocationIds?: Record<string, string | null>;
}

export function useLocationSelectionHandler<T extends LocationSelectionState>(
  setState: Dispatch<SetStateAction<T>>,
): (
  oldAddressId: string,
  newAddressId: string,
  newExternalLocationId: string | undefined,
  index: number,
) => void {
  const team = useContext(TeamContext);

  return (oldAddressId, newAddressId, newExternalLocationId, index) => {
    setState((prev) => {
      const newLocations = prev.locations.map((id, i) =>
        i === index ? newAddressId : id,
      );

      const newRestockIds = { ...prev.restockExternalLocationIds };

      let defaultRestockId: string | null;

      // For non-Shopify merchants, always use NO_RESTOCK since we don't support it yet.
      if (team?.platform !== Provider.SHOPIFY) {
        defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;
      } else {
        switch (newAddressId) {
          case SPECIAL_LOCATION_ID.ORIGINAL: {
            defaultRestockId = SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT;
            break;
          }
          case SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN: {
            defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;
            break;
          }
          case SPECIAL_LOCATION_ID.REDO_MANAGED: {
            defaultRestockId = SPECIAL_RESTOCK_ID.NO_RESTOCK;
            break;
          }
          default:
            defaultRestockId =
              newExternalLocationId ?? SPECIAL_RESTOCK_ID.BACK_TO_FULFILLMENT;
        }
      }

      newRestockIds[newAddressId] = defaultRestockId;

      if (oldAddressId !== newAddressId && oldAddressId in newRestockIds) {
        delete newRestockIds[oldAddressId];
      }

      return {
        ...prev,
        locations: newLocations,
        restockExternalLocationIds: newRestockIds,
      };
    });
  };
}
